/**
 * 📊 AffiliateDashboardPage - Übersicht / Homepage des Affiliate Portals
 * 
 * Sections:
 * - KPI Row (Provision, Ausstehend, Lifetime, Nächste Auszahlung)
 * - Earnings Chart
 * - Referral Link Card
 * - Quick Stats
 * - Konstantleiter Progress
 * - Activity Feed
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { KPIStatCard } from '@/components/affiliate/cards/KPIStatCard';
import { ProgressCard, type AffiliateLevel } from '@/components/affiliate/cards/ProgressCard';
import { ReferralLinkCard } from '@/components/affiliate/cards/ReferralLinkCard';
import { EarningsChart } from '@/components/affiliate/charts/EarningsChart';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { 
  SkeletonKPICard, 
  SkeletonChart, 
  SkeletonList,
  SkeletonStatCard,
  SkeletonProgressCard 
} from '@/components/affiliate/skeletons';
import { useAffiliateDashboard } from '@/features/affiliate/hooks';
import { 
  Wallet, 
  TrendingUp,
  Calendar,
  Users,
  CheckCircle2,
  ArrowRight,
  Star,
  Clock,
  Utensils,
  Sparkles,
  ImageIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Activity } from '@/lib/affiliate-mock-data';

// ============================================
// SUB-COMPONENTS
// ============================================

interface ActivityItemProps {
  activity: Activity;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
  return (
    <div className="flex items-center gap-4 py-3.5 px-1 rounded-xl transition-colors hover:bg-emerald-50/50 group">
      <div className={cn(
        'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
        'bg-gradient-to-br shadow-sm transition-transform group-hover:scale-105',
        activity.type === 'lead' && 'from-blue-500/20 to-blue-500/10',
        activity.type === 'conversion' && 'from-green-500/20 to-green-500/10',
        activity.type === 'payout' && 'from-gray-500/20 to-gray-500/10',
        activity.type === 'level' && 'from-amber-500/20 to-amber-500/10',
        activity.type === 'commission' && 'from-emerald-500/20 to-emerald-500/10',
      )}>
        {activity.type === 'lead' && <Users className="w-4 h-4 text-blue-600" />}
        {activity.type === 'conversion' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
        {activity.type === 'payout' && <Wallet className="w-4 h-4 text-gray-600" />}
        {activity.type === 'level' && <Star className="w-4 h-4 text-amber-500" />}
        {activity.type === 'commission' && <TrendingUp className="w-4 h-4 text-emerald-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
        <p className="text-xs text-gray-500 truncate">{activity.description}</p>
      </div>
      <div className="text-right shrink-0 flex flex-col items-end gap-1">
        {activity.amount && (
          <span className="text-sm font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
            +€{activity.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
          </span>
        )}
        <p className="text-xs text-gray-400">{activity.timestamp}</p>
      </div>
    </div>
  );
};

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateDashboardPage: React.FC = () => {
  const { data, isLoading, copyReferralLink } = useAffiliateDashboard();
  const { toast } = useToast();
  
  // Handler für ReferralLinkCard
  const handleCopyLink = async () => {
    const success = await copyReferralLink();
    toast({
      title: success ? '✅ Link kopiert!' : '❌ Kopieren fehlgeschlagen',
      description: success ? 'Dein Referral-Link wurde in die Zwischenablage kopiert.' : 'Bitte kopiere den Link manuell.',
      variant: success ? 'default' : 'destructive',
    });
  };
  
  const handleShowQR = () => {
    toast({
      title: '📱 QR-Code',
      description: 'QR-Code Generierung wird bald verfügbar sein.',
    });
  };
  
  const handleShare = (platform: string) => {
    const shareUrl = data?.referralLink ? `https://${data.referralLink}` : '';
    const shareText = 'Entdecke SpeiseGuru – Das Partner-Programm für Restaurants!';
    
    const urls: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      email: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    };
    
    const url = urls[platform];
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast({ title: `Teilen über ${platform}`, description: 'Plattform wird bald unterstützt.' });
    }
  };

  // Loading State
  if (isLoading || !data) {
    return (
      <AffiliateLayout>
        <div className="space-y-6">
          {/* KPI Skeletons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <SkeletonKPICard key={i} />)}
          </div>
          {/* Chart + Card Skeletons */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SkeletonChart height={280} />
            </div>
            <SkeletonStatCard />
          </div>
          {/* Bottom Grid Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SkeletonStatCard />
            <SkeletonProgressCard />
            <SkeletonList items={4} />
          </div>
        </div>
      </AffiliateLayout>
    );
  }

  const { kpis, levelProgress, activities, referralLink, earningsChart } = data;

  // Map level number to AffiliateLevel
  const levelMap: Record<number, AffiliateLevel> = {
    1: 'starter',
    2: 'bronze',
    3: 'silber',
    4: 'gold',
    5: 'platin',
    6: 'diamant',
  };

  // Mock data for "Ihre Vorteile als Partner" section
  const partnerBenefits = [
    { id: 1, restaurant: 'Restaurant zhüngt', amount: 398.95, icon: Utensils, type: 'signup' },
    { id: 2, restaurant: 'Restaurant verlängert', amount: 76.34, icon: CheckCircle2, type: 'renewal' },
    { id: 3, restaurant: 'Flyer Design publiziert', amount: 50, icon: ImageIcon, type: 'addon' },
  ];

  return (
    <AffiliateLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* KPI Row - 2 cols on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPIStatCard
            label="Provision diesen Monat"
            value={`€${kpis.monthlyEarnings.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`}
            trend={{ value: 32, direction: 'up' }}
            icon={Wallet}
            iconVariant="primary"
          />
          <KPIStatCard
            label="Ausstehende Provision"
            value={`€${(kpis.totalEarnings * 0.12).toLocaleString('de-DE', { minimumFractionDigits: 2 })}`}
            icon={Clock}
            iconVariant="warning"
          />
          <KPIStatCard
            label="Lifetime Earnings"
            value={`€${kpis.totalEarnings.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`}
            trend={{ value: 36, direction: 'up' }}
            icon={TrendingUp}
            iconVariant="success"
          />
          {/* Nächste Auszahlung Card - Special Design */}
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-2xl shadow-lg shadow-emerald-500/25 overflow-hidden">
            <CardContent className="p-4 sm:p-5 md:p-6 h-full flex flex-col justify-between">
              <div className="flex items-center justify-between mb-2">
                <span className="text-emerald-100 text-xs sm:text-sm font-medium">Nächste Auszahlung</span>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-200" />
                  <span className="text-sm sm:text-base font-semibold text-emerald-100">05. März</span>
                </div>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold mt-1">
                  €{(kpis.totalEarnings * 0.12 + 120).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left: Chart (2 cols) */}
          <div className="lg:col-span-2">
            <EarningsChart 
              data={earningsChart}
              title="Einnahmen-Entwicklung"
              subtitle="Umsatz deiner Restaurants und deine Provision"
              trend={{ value: 18, label: 'vs. Vormonat' }}
            />
          </div>
          
          {/* Right: Referral Link */}
          <div>
            <ReferralLinkCard
              link={`https://${referralLink}`}
              shortCode="martink"
              onCopy={handleCopyLink}
              onShowQR={handleShowQR}
              onShare={handleShare}
              stats={{
                clicks: 248,
                conversions: 7,
                conversionRate: 2.8,
              }}
            />
          </div>
        </div>
        
        {/* Bottom Grid - Stack on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Ihre Vorteile als Partner - NEW */}
          <Card className="bg-white/80 backdrop-blur-xl border-white/60 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
            <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base font-semibold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Ihre Vorteile als Partner</CardTitle>
                <Sparkles className="w-4 h-4 text-amber-500" />
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="space-y-3">
                {partnerBenefits.map((benefit) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={benefit.id} className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-gray-50 to-white hover:from-emerald-50/50 hover:to-white transition-colors group">
                      <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                        benefit.type === 'signup' && 'bg-emerald-100 text-emerald-600',
                        benefit.type === 'renewal' && 'bg-blue-100 text-blue-600',
                        benefit.type === 'addon' && 'bg-purple-100 text-purple-600',
                      )}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{benefit.restaurant}</p>
                        <p className="text-xs text-gray-500">
                          {benefit.type === 'signup' && 'Neues Lokal'}
                          {benefit.type === 'renewal' && 'Verlängerung'}
                          {benefit.type === 'addon' && 'Zusatzleistung'}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-emerald-600">
                        +€{benefit.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                })}
              </div>
              <Button variant="ghost" size="sm" className="w-full mt-3 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 font-medium">
                Alle Aktivitäten anzeigen <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
          
          {/* Konstantleiter */}
          <ProgressCard
            level={levelMap[levelProgress.currentLevel] || 'silber'}
            nextLevel={levelMap[levelProgress.currentLevel + 1] || 'gold'}
            progress={levelProgress.progress}
            currentValue={levelProgress.progress}
            requiredValue={100}
            unit="%"
            nextLevelBonus={{
              percentage: 12,
              label: 'Provision auf alle Umsätze',
            }}
          />
          
          {/* Activity Feed */}
          <Card className="bg-white/80 backdrop-blur-xl border-white/60 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
            <CardHeader className="pb-1 sm:pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base font-semibold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Aktivitäten</CardTitle>
                <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 -mr-2 font-medium text-xs sm:text-sm h-9 min-h-[44px] px-2">
                  Alle <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-4 pt-0 pb-2 sm:pb-4">
              {activities.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-500">Noch keine Aktivitäten</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {activities.slice(0, 4).map(activity => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AffiliateLayout>
  );
};

export default AffiliateDashboardPage;
